/* eslint-disable unicorn/filename-case */
import { useNavigate } from '@tanstack/react-router';
import {
  type ReactNode,
  createContext,
  useContext,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';

import { ToastVariant } from '../../components/toast';
import { TrialSubscriptionModal } from '../../components/trial-subscription-modal';
import { getConfig } from '../../config';
import { ContentType } from '../../entity/api/content-funnel';
import { PlanError } from '../../entity/api/plan';
import { ForbiddenError } from '../../error';
import { convertError } from '../../error/convert';
import { trackError } from '../../error/tracking';
import { captureException } from '../../logger/capture-exception';
import { getQueryClient } from '../../sdk/request';
import { PostHogEvents, capturePostHogEvent } from '../../services/posthog';
import {
  articlesKeys,
  useAuthUser,
  useCreateArticle,
  useOneClickGenerate,
  useOrganizationBilling,
  useRefreshArticle,
} from '../../services/storage';
import type { ArticleWithSeo } from '../../services/storage/articles-queries';
import { useBrandsList } from '../../services/storage/brands-queries';
import { useEndSubscriptionTrial } from '../../services/storage/organization-billing-queries';
import {
  fetchOrganizationByIdImperative,
  useOrganizationsList,
} from '../../services/storage/organizations-queries';
import { seoStrategyKeys } from '../../services/storage/seo-strategy-queries';
import { tGlobal, tNamespace } from '../../services/translation';
import { getSelectedBrand } from '../../shared/brand';
import { useToast } from '../../shared/contexts/toast-context';
import { canOrgChangeArticles, getSelectedOrganization } from '../../shared/organization';
import { isOrgCancelled, isNotSubscribed as orgIsNotSubscribed } from '../../shared/subscription';
import { BrandNotFilledModal } from './_sidebar-layout.blogs/-brand-not-filled-modal';
import { ModalRewrite } from './_sidebar-layout.blogs/-modal-rewrite';
import { ModalGeneration } from './_sidebar-layout.blogs/-section-modal-generation';
import { TrialLimitPlanModal } from './settings/_sidebar-settings-layout/organization.$id/-components/trial-limit-plan-modal';

const t = tNamespace('app.index');
const tErrors = tNamespace('errors');

type ContentModalsContextValue = {
  openGenerationModal: (opts?: { keyword?: string; topic?: string }) => void;
  openRewriteModal: (opts?: { url?: string }) => void;
  openTrialLimitModal: () => void;
};

const ContentModalsContext = createContext<ContentModalsContextValue | null>(null);

export function useContentModals(): ContentModalsContextValue {
  const ctx = useContext(ContentModalsContext);
  if (ctx == null) {
    throw new Error('useContentModals must be used inside ContentModalsProvider');
  }
  return ctx;
}

export function ContentModalsProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const queryClient = getQueryClient();
  const { defaultCountry, defaultLanguage } = getConfig();
  const addToast = useToast();

  const { data: currentUser } = useAuthUser();
  const currentOrg = getSelectedOrganization(currentUser);
  const currentBrand = getSelectedBrand(currentUser);
  const { data: orgBilling } = useOrganizationBilling(currentOrg?._id);
  const { data: brandsList } = useBrandsList();
  const { mutateAsync: endTrialMutation } = useEndSubscriptionTrial();
  const { data: organizationsList } = useOrganizationsList();

  // Read from whatever articles query is already cached for this brand — no extra request.
  // Reactively updates whenever the blogs page (or any other consumer) populates the cache.
  const articlesFromCache = useSyncExternalStore(
    function subscribe(callback) {
      return queryClient.getQueryCache().subscribe(callback);
    },
    function getSnapshot() {
      if (currentBrand?._id == null) {
        return null;
      }
      const entries = queryClient.getQueriesData<{
        articles: ArticleWithSeo[];
        totalCount: number;
      }>({ queryKey: articlesKeys.byBrandId(currentBrand._id) });
      for (const [, data] of entries) {
        if (data != null) {
          return data;
        }
      }
      return null;
    },
    function getServerSnapshot() {
      return null;
    }
  );
  const articleList = articlesFromCache?.articles ?? [];
  const hasZeroArticles = articlesFromCache != null && articleList.length === 0;

  const shouldShowTooltips = useMemo(
    function () {
      if (organizationsList == null || brandsList == null || currentOrg == null) {
        return false;
      }

      const orgBrands = brandsList.filter(function (brand) {
        return brand?.organizationId === currentOrg._id;
      });

      return orgBrands.length === 1 && articleList.length === 0;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [organizationsList, brandsList, currentOrg, articleList.length]
  );

  const createArticleMutation = useCreateArticle();
  const oneClickGenerateMutation = useOneClickGenerate();
  const refreshArticleMutation = useRefreshArticle(currentBrand?._id ?? '', currentOrg?._id ?? '');
  const createContentLoading =
    createArticleMutation.isPending || oneClickGenerateMutation.isPending;

  const [isModalGenerationOpen, setIsModalGenerationOpen] = useState(false);
  const [isRewriteDialogOpen, setIsRewriteDialogOpen] = useState(false);
  const [isTrialLimitPlanModalOpen, setIsTrialLimitPlanModalOpen] = useState(false);
  const [isTrialSubscriptionModalOpen, setIsTrialSubscriptionModalOpen] = useState(false);
  const [brandFillerModalOpen, setBrandFillerModalOpen] = useState(false);
  const [generationMode, setGenerationMode] = useState<'create' | 'rewrite'>('create');
  const [keyword, setKeyword] = useState('');
  const [topic, setTopic] = useState('');
  const [rewriteInitialUrl, setRewriteInitialUrl] = useState('');

  // Refs used by callers that need to track if a pending seed value was applied
  const pendingKeywordRef = useRef<string | null>(null);
  const pendingRewriteUrlRef = useRef<string | null>(null);
  const shouldReopenGenerationModalRef = useRef(false);

  const isCancelled = isOrgCancelled(orgBilling);
  const isNotSubscribed = orgIsNotSubscribed(orgBilling);

  const canCreateContent = (function () {
    if (isCancelled) {
      return false;
    }
    const brand = brandsList?.[0];
    if (brand == null) {
      return false;
    }
    const { _id: brandId, organizationId } = brand;
    return (
      typeof brandId === 'string' &&
      brandId.length > 0 &&
      typeof organizationId === 'string' &&
      organizationId.length > 0
    );
  })();

  function getLanguage() {
    const hasBrand = brandsList != null && brandsList.length > 0 && currentBrand != null;
    const brand = hasBrand
      ? brandsList.find(function (b) {
          return b?._id === currentBrand?._id;
        })
      : undefined;
    return brand?.language?.trim() ?? defaultLanguage;
  }

  function getCountry() {
    const hasBrand = brandsList != null && brandsList.length > 0 && currentBrand != null;
    const brand = hasBrand
      ? brandsList.find(function (b) {
          return b?._id === currentBrand?._id;
        })
      : undefined;
    return brand?.country?.trim() ?? defaultCountry;
  }

  const language = getLanguage();
  const country = getCountry();

  function handleDialogState(open: boolean) {
    setKeyword('');
    setTopic('');
    setIsModalGenerationOpen(open);
  }

  function openModalAfterGate(type: 'create' | 'rewrite') {
    if (isCancelled) {
      return;
    }

    if (isNotSubscribed && !canOrgChangeArticles(orgBilling ?? null)) {
      setIsTrialSubscriptionModalOpen(true);
      return;
    }

    if (currentOrg == null) {
      addToast({
        title: tGlobal('somethingWentWrong'),
        variant: ToastVariant.Error,
        id: Date.now(),
      });
      return;
    }

    if (currentBrand?.excerpt == null || currentBrand?.excerpt?.length === 0) {
      setGenerationMode(type);
      setBrandFillerModalOpen(true);
      return;
    }

    if (type === 'create') {
      handleDialogState(true);
      const pending = pendingKeywordRef.current;
      if (pending != null && pending.length > 0) {
        setKeyword(pending);
        pendingKeywordRef.current = null;
      }
    } else {
      const pending = pendingRewriteUrlRef.current;
      if (pending != null && pending.length > 0) {
        setRewriteInitialUrl(pending);
        pendingRewriteUrlRef.current = null;
      }
      setIsRewriteDialogOpen(true);
    }
  }

  function openGenerationModal(opts?: { keyword?: string; topic?: string }) {
    pendingKeywordRef.current = opts?.keyword ?? null;
    if (opts?.topic != null && opts.topic.length > 0) {
      setTopic(opts.topic);
    }
    openModalAfterGate('create');
  }

  function openRewriteModal(opts?: { url?: string }) {
    pendingRewriteUrlRef.current = opts?.url ?? null;
    openModalAfterGate('rewrite');
  }

  async function handleCreateContent(
    kw: string,
    prompt: string,
    lang: string,
    ctry: string,
    tp?: string | null
  ): Promise<void> {
    if (!canCreateContent) {
      return;
    }

    try {
      await createArticleMutation.mutateAsync({
        topic: tp ?? '',
        title: '',
        body: '',
        primaryKeyword: kw,
        primaryPrompt: prompt ?? '',
        secondaryKeywords: [],
        language: lang,
        country: ctry,
        description: '',
        metaTitle: '',
        metaDescription: '',
        brandId: currentBrand?._id ?? '',
        organizationId: currentOrg?._id ?? '',
      });
      capturePostHogEvent(PostHogEvents.CONTENT_CREATED, {
        mode: '3_steps',
        keyword: kw,
        topic: tp,
        language: lang,
        country: ctry,
        brandId: currentBrand?._id ?? '',
        organizationId: currentOrg?._id ?? '',
      });

      setIsModalGenerationOpen(false);

      if (currentBrand?._id != null) {
        void queryClient.invalidateQueries({
          queryKey: seoStrategyKeys.actionPlan(currentBrand._id),
        });
        void queryClient.invalidateQueries({
          queryKey: ['seo-strategy', 'keywords', currentBrand._id],
        });
      }

      if (typeof currentOrg?._id === 'string') {
        await fetchOrganizationByIdImperative(queryClient, currentOrg._id);
      }
    } catch (err) {
      const handledErr = convertError(err);
      if (handledErr instanceof ForbiddenError) {
        if ((handledErr.originalMessage as PlanError) === PlanError.LimitArticleTrialing) {
          setKeyword(kw);
          setTopic(tp ?? '');
          setIsModalGenerationOpen(false);
          shouldReopenGenerationModalRef.current = true;
          setIsTrialLimitPlanModalOpen(true);
          return;
        }

        addToast({
          variant: ToastVariant.Error,
          title: tErrors(handledErr.originalMessage),
          id: Date.now(),
        });
        return;
      }

      addToast({
        title: t('errorCreateContent'),
        description: t('errorCreateContentDescription'),
        variant: ToastVariant.Error,
        id: Date.now(),
      });
      captureException(
        err,
        {
          file: '_content-modals-context.tsx',
          method: 'handleCreateContent',
          infoMessage: 'unable to create content',
        },
        trackError
      );
    }
  }

  async function handleGenerateInOneClick(
    tp: string,
    kw: string,
    prompt: string,
    lang: string,
    ctry: string
  ): Promise<void> {
    if (!canCreateContent) {
      return;
    }

    try {
      capturePostHogEvent(PostHogEvents.CONTENT_CREATED, {
        mode: '1_step',
        contentType: ContentType.BlogPost,
        brandId: currentBrand?._id ?? '',
        topic: tp,
        keyword: kw,
        primaryPrompt: prompt ?? '',
      });

      await oneClickGenerateMutation.mutateAsync({
        brandId: currentBrand?._id ?? '',
        topic: tp,
        keyword: kw,
        primaryPrompt: prompt ?? '',
        language: lang,
        country: ctry,
      });

      setIsModalGenerationOpen(false);

      if (currentBrand?._id != null) {
        void queryClient.invalidateQueries({
          queryKey: seoStrategyKeys.actionPlan(currentBrand._id),
        });
        void queryClient.invalidateQueries({
          queryKey: ['seo-strategy', 'keywords', currentBrand._id],
        });
      }

      capturePostHogEvent(PostHogEvents.FUNNEL_CONTENT_GENERATING, {
        brandId: currentBrand?._id ?? '',
        topic: tp,
        keyword: kw,
        primaryPrompt: prompt ?? '',
      });
    } catch (err) {
      const handledErr = convertError(err);
      if (handledErr instanceof ForbiddenError) {
        if ((handledErr.originalMessage as PlanError) === PlanError.LimitArticleTrialing) {
          setKeyword(kw);
          setTopic(tp);
          setIsModalGenerationOpen(false);
          shouldReopenGenerationModalRef.current = true;
          setIsTrialLimitPlanModalOpen(true);
          return;
        }

        addToast({
          variant: ToastVariant.Error,
          title: tErrors(handledErr.originalMessage),
          id: Date.now(),
        });
        return;
      }

      addToast({
        title: t('errorCreateContent'),
        description: t('errorCreateContentDescription'),
        variant: ToastVariant.Error,
        id: Date.now(),
      });
      captureException(
        err,
        {
          file: '_content-modals-context.tsx',
          method: 'handleGenerateInOneClick',
          infoMessage: 'unable to create content one click',
        },
        trackError
      );
    }
  }

  async function handleRewrite(params: {
    country: string;
    language: string;
    mode: string;
    chosenTitle: string;
    url: string | undefined | null;
    tiptapJsonContent: Record<string, unknown> | undefined | null;
    extraInformation: string;
    numberOfResults: number;
    primaryPrompt: string;
    primaryKeyword: string;
    targetWordCount: string | number;
  }): Promise<void> {
    if (!canCreateContent) {
      return;
    }

    try {
      const rewriteResult = await refreshArticleMutation.mutateAsync({
        brandId: currentBrand?._id ?? '',
        ...params,
      });

      capturePostHogEvent(PostHogEvents.CONTENT_REWRITTEN, {
        contentId: rewriteResult.contentId,
        rewriteScope: params.mode === 'major' ? 'full' : 'partial',
      });

      setIsRewriteDialogOpen(false);

      if (currentBrand?._id != null) {
        void queryClient.invalidateQueries({
          queryKey: seoStrategyKeys.actionPlan(currentBrand._id),
        });
        void queryClient.invalidateQueries({
          queryKey: ['seo-strategy', 'keywords', currentBrand._id],
        });
      }
    } catch (err) {
      const handledErr = convertError(err);
      if (handledErr instanceof ForbiddenError) {
        if ((handledErr.originalMessage as PlanError) === PlanError.LimitArticleTrialing) {
          setIsRewriteDialogOpen(false);
          shouldReopenGenerationModalRef.current = true;
          setIsTrialLimitPlanModalOpen(true);
          return;
        }

        addToast({
          variant: ToastVariant.Error,
          title: tErrors(handledErr.originalMessage),
          id: Date.now(),
        });
        return;
      }

      addToast({
        title: t('errorCreateContent'),
        description: t('errorCreateContentDescription'),
        variant: ToastVariant.Error,
        id: Date.now(),
      });
      captureException(
        err,
        {
          file: '_content-modals-context.tsx',
          method: 'handleRewrite',
          infoMessage: 'unable to rewrite content',
        },
        trackError
      );
    }
  }

  async function onEndSubscriptionTrial() {
    if (currentOrg?._id == null) {
      setIsTrialLimitPlanModalOpen(false);
      return;
    }

    try {
      await endTrialMutation(currentOrg._id);
      addToast({
        title: t('trialEndedSuccess'),
        variant: ToastVariant.Success,
        id: Date.now(),
      });
    } catch (err) {
      addToast({
        title: tGlobal('somethingWentWrong'),
        variant: ToastVariant.Error,
        id: Date.now(),
      });
      captureException(
        err,
        {
          file: '_content-modals-context.tsx',
          method: 'onEndSubscriptionTrial',
          infoMessage: 'unable to end subscription trial',
          organizationId: currentOrg._id,
        },
        trackError
      );
    } finally {
      setIsTrialLimitPlanModalOpen(false);
      if (shouldReopenGenerationModalRef.current) {
        setIsModalGenerationOpen(true);
      }
      shouldReopenGenerationModalRef.current = false;
    }
  }

  function openTrialLimitModal() {
    shouldReopenGenerationModalRef.current = false;
    setIsTrialLimitPlanModalOpen(true);
  }

  return (
    <ContentModalsContext.Provider
      value={{ openGenerationModal, openRewriteModal, openTrialLimitModal }}
    >
      {children}
      {isModalGenerationOpen ? (
        <ModalGeneration
          keyword={keyword}
          topic={topic}
          country={country}
          language={language}
          isOpen={isModalGenerationOpen}
          onOpenChange={handleDialogState}
          generateOneClick={handleGenerateInOneClick}
          createContent={handleCreateContent}
          isLoading={createContentLoading}
          brandId={currentBrand?._id ?? ''}
          hasZeroArticles={hasZeroArticles}
          shouldShowTooltips={shouldShowTooltips}
        />
      ) : null}
      {isTrialLimitPlanModalOpen ? (
        <TrialLimitPlanModal
          open={isTrialLimitPlanModalOpen}
          onOpenChange={setIsTrialLimitPlanModalOpen}
          organizationBilling={orgBilling}
          userLanguage={currentUser?.language ?? language}
          onConfirm={onEndSubscriptionTrial}
          onSkip={function () {
            setIsTrialLimitPlanModalOpen(false);
          }}
        />
      ) : null}
      {isRewriteDialogOpen ? (
        <ModalRewrite
          isOpen={isRewriteDialogOpen}
          onOpenChange={function (open) {
            if (!open) {
              setRewriteInitialUrl('');
            }
            setIsRewriteDialogOpen(open);
          }}
          onRewrite={handleRewrite}
          brandId={currentBrand?._id ?? ''}
          initialUrl={rewriteInitialUrl}
        />
      ) : null}
      {brandFillerModalOpen ? (
        <BrandNotFilledModal
          open={brandFillerModalOpen}
          onOpenChange={setBrandFillerModalOpen}
          createAnyway={function () {
            setBrandFillerModalOpen(false);
            if (generationMode === 'create') {
              setIsModalGenerationOpen(true);
            } else {
              setIsRewriteDialogOpen(true);
            }
          }}
          addBrandContext={function () {
            setBrandFillerModalOpen(false);
            navigate({
              to: '/app/brand-context',
              search: { brandId: currentBrand?._id ?? '' },
            }).catch(function (err) {
              captureException(
                err,
                {
                  file: '_content-modals-context.tsx',
                  method: 'addBrandContext',
                  infoMessage: 'unable to navigate to /app/brand-context',
                },
                trackError
              );
            });
          }}
        />
      ) : null}
      {isTrialSubscriptionModalOpen ? (
        <TrialSubscriptionModal
          open={isTrialSubscriptionModalOpen}
          onOpenChange={setIsTrialSubscriptionModalOpen}
          orgId={currentOrg?._id ?? ''}
        />
      ) : null}
    </ContentModalsContext.Provider>
  );
}
